import { useOutletContext } from "react-router";

import type { OrgRecord } from "@/shared/domain/orgs";

/**
 * Shape of the dataset-detail state that `<Dataset />` publishes through
 * `<Outlet context>` to its nested tab routes (file browser, events,
 * permissions, sessions, etc.). Carries the dataset id resolved from the
 * URL and the active organization.
 *
 * Consumers that need the dataset record itself should call
 * `useDataset(datasetId)` directly — TanStack Query dedupes by id, so
 * parent and child calls share a single in-flight request and cache entry.
 */
export type DatasetContext = {
  datasetId: string;
  org: OrgRecord | null;
};

/**
 * Typed accessor for the dataset outlet context. Call from any descendant
 * route of `<Dataset />` to read the shared parent-derived values
 * (datasetId, org) as a single typed bundle.
 *
 * Throws if called outside a `<Dataset />` outlet — react-router's
 * `useOutletContext` returns `null` in that case, which the hook surfaces
 * as a named error so the misuse points at this hook rather than at a
 * downstream destructure.
 */
export function useDatasetContext(): DatasetContext {
  const context = useOutletContext<DatasetContext | null>();
  if (context === null) {
    throw new Error(
      "useDatasetContext must be called inside a <Dataset /> outlet",
    );
  }
  return context;
}
